<template>
  <v-menu location="bottom start" :offset="6" :close-on-content-click="!multiple" content-class="k-glass-3 task-picker__menu">
    <template v-slot:activator="{ props }">
      <button v-bind="props" type="button" class="task-picker" :class="{ 'task-picker--set': hasValue }">
        <v-icon :icon="icon" size="16" />
        <span class="task-picker__label">{{ display }}</span>
        <v-icon icon="mdi-chevron-down" size="14" />
      </button>
    </template>

    <div class="task-picker__panel">
      <KSearch v-if="searchable" v-model="query" autofocus class="mb-2 task-picker__search" />
      <v-list class="bg-transparent task-picker__list" density="compact" nav>
        <v-list-item
          v-if="clearable && !multiple && hasValue"
          :title="clearLabel"
          rounded="lg"
          prepend-icon="mdi-close"
          @click="$emit('update:modelValue', null)"
        />
        <v-list-item
          v-for="item in shown"
          :key="item.value"
          :title="item.title"
          :subtitle="item.subtitle"
          :active="isActive(item)"
          color="primary"
          rounded="lg"
          @click="toggle(item)"
        >
          <template v-if="item.initials || item.image || item.color" v-slot:prepend>
            <v-avatar v-if="item.initials || item.image" size="28" class="mr-3" :color="item.image ? undefined : 'primary'" variant="tonal">
              <v-img v-if="item.image" cover :src="item.image" />
              <span v-else class="text-caption">{{ item.initials }}</span>
            </v-avatar>
            <span v-else class="task-picker__dot" :style="{ background: item.color }" />
          </template>
          <template v-if="multiple" v-slot:append>
            <v-icon v-if="isActive(item)" icon="mdi-check" size="18" color="primary" />
          </template>
        </v-list-item>
        <div v-if="!shown.length" class="task-picker__empty">Aucun résultat</div>
      </v-list>
    </div>
  </v-menu>
</template>

<script>
import KSearch from "@/components/ui/KSearch";

const LIMIT = 40;

export default {
  name: "TaskPicker",
  components: { KSearch },
  props: {
    modelValue: {
      type: [String, Number, Array],
      default: null,
    },
    items: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
      default: "mdi-tag-outline",
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    clearLabel: {
      type: String,
      default: "Aucun",
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      query: "",
    };
  },
  computed: {
    selected() {
      if (this.multiple) return Array.isArray(this.modelValue) ? this.modelValue : [];
      return this.modelValue === null || this.modelValue === undefined || this.modelValue === "" ? [] : [this.modelValue];
    },
    hasValue() {
      return this.selected.length > 0;
    },
    shown() {
      const term = this.query.trim().toLowerCase();
      const rows = term ? this.items.filter((item) => `${item.title} ${item.subtitle || ""}`.toLowerCase().includes(term)) : this.items;
      return rows.slice(0, LIMIT);
    },
    display() {
      if (!this.hasValue) return this.label;
      const titles = this.selected.map((value) => this.items.find((item) => item.value === value)?.title).filter(Boolean);
      if (!titles.length) return this.label;
      if (this.multiple) return titles.length > 2 ? `${titles.length} ${this.label.toLowerCase()}` : titles.join(", ");
      return titles[0];
    },
  },
  methods: {
    isActive(item) {
      return this.selected.includes(item.value);
    },
    toggle(item) {
      if (!this.multiple) {
        this.$emit("update:modelValue", item.value);
        this.query = "";
        return;
      }
      const next = this.isActive(item) ? this.selected.filter((value) => value !== item.value) : [...this.selected, item.value];
      this.$emit("update:modelValue", next);
    },
  },
};
</script>

<style scoped>
.task-picker {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  max-width: 100%;
  padding: 0 12px;
  border: 1px solid var(--k-glass-border);
  border-radius: 999px;
  background: transparent;
  color: inherit;
  font-size: 13px;
  cursor: pointer;
  transition: background var(--k-dur-fast);
}

.task-picker:hover {
  background: var(--k-track);
}

.task-picker--set {
  border-color: rgba(var(--v-theme-accent), 0.55);
  background: rgba(var(--v-theme-accent), 0.14);
}

.task-picker__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.task-picker__panel {
  width: 280px;
  max-height: 340px;
  padding: 10px;
  overflow-y: auto;
}

.task-picker__dot {
  width: 9px;
  height: 9px;
  margin-right: 14px;
  border-radius: 50%;
}

.task-picker__search {
  flex: none;
  width: 100%;
}

.task-picker__empty {
  padding: 12px;
  font-size: 13px;
  opacity: 0.6;
}
</style>
